/**
 * Dropdown cell editor built with the wrapper's `Editor()` helper, which mounts
 * a real React component — and therefore real MUI components — as the editor.
 *
 * Two things to know about this seam:
 *
 *  1. `Editor()` mounts the component in its OWN React root (createRoot), so it
 *     is NOT inside the app's <ThemeProvider>. React context does not cross
 *     roots, which means MUI components here would fall back to the default MUI
 *     theme — Roboto, blue primary — instead of ours. `setEditorTheme()` is the
 *     bridge: RevoGridTable pushes the live theme into this module and the
 *     editor re-provides it. There is no provider prop on the grid to do it
 *     properly.
 *  2. RevoGrid does not know the option list. `save(value)` writes whatever it
 *     is handed, and every *bulk* write path (fill handle, clipboard paste,
 *     setDataAt) bypasses the editor entirely, so a column with a fixed option
 *     list needs its own validation on those paths.
 */
import { useRef, useState } from 'react';
import { Editor } from '@revolist/react-datagrid';
import type { EditorType } from '@revolist/react-datagrid';
import { Box, MenuItem, Select, ThemeProvider, createTheme } from '@mui/material';
import type { Theme } from '@mui/material';

/** An option is either a plain string, or a label shown for a stored value. */
export type SelectOption = string | { label: string; value: string };

export const toSelectOption = (option: SelectOption) =>
  typeof option === 'string' ? { label: option, value: option } : option;

/** Label to show for a stored value — the value itself when it is not in the list. */
export const getSelectOptionLabel = (options: readonly SelectOption[], value: unknown) => {
  const stored = (value ?? '') as string;
  return options.map(toSelectOption).find((option) => option.value === stored)?.label ?? stored;
};

/**
 * The live app theme, pushed in by RevoGridTable. Module-level because the
 * editor renders in a detached React root and cannot read context — see note 1.
 */
let sharedTheme: Theme | null = null;
const fallbackTheme = createTheme();

export const setEditorTheme = (theme: Theme) => {
  sharedTheme = theme;
};

type SelectEditorProps = EditorType & { options: readonly SelectOption[] };

/**
 * Geometry copied from the MUI form this replaces: `EditGatewayTableRow` uses
 * `size="small"` + `maxHeight: '37.11px'` + 14px text, inside a cell with MUI's
 * 16px gutter. Shared with the resting-state replica so the two line up.
 */
export const FIELD_HEIGHT = 37;
/** RHFSelect's menu: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP */
const MENU_MAX_HEIGHT = 48 * 4.5 + 8;

const SelectCellEditor = ({ column, val, save, close, options }: SelectEditorProps) => {
  // `val` is the pending editor value (RevoGrid pre-seeds it when editing starts
  // from a keypress); `column.value` is what is currently in the model.
  const initial = (val ?? column?.value ?? '') as string;
  const [open, setOpen] = useState(true);
  // save() already closes the editor and moves focus; calling close() after it
  // would fight that, so remember whether we saved.
  const savedRef = useRef(false);
  const items = options.map(toSelectOption);

  return (
    <ThemeProvider theme={sharedTheme ?? fallbackTheme}>
      {/*
        revogr-edit covers the whole cell, so the field is centred inside it with
        the same 16px gutter the cells use — otherwise the outline would sit
        flush against the cell edges, which no MUI table field does.
      */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          px: 2,
          boxSizing: 'border-box',
        }}
      >
        <Select
          open={open}
          autoFocus
          fullWidth
          size="small"
          // default variant — outlined, same as RHFSelect in the v1 form.
          // A value outside the list would make MUI warn and render blank
          // anyway, so surface it as "nothing selected" instead.
          value={items.some((item) => item.value === initial) ? initial : ''}
          onChange={(event) => {
            savedRef.current = true;
            save(event.target.value);
          }}
          onClose={() => {
            setOpen(false);
            if (!savedRef.current) {
              close();
            }
          }}
          sx={{
            height: FIELD_HEIGHT,
            fontSize: (theme) => theme.typography.body2.fontSize,
            backgroundColor: (theme) => theme.palette.background.default,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              minHeight: 'unset',
              py: 0,
            },
          }}
          MenuProps={{
            // mirrors RHFSelect's MenuProps; the dropdown shadow itself comes
            // from the app theme's MuiPopover override, which rides along with
            // the ThemeProvider above
            PaperProps: {
              style: {
                maxHeight: MENU_MAX_HEIGHT,
                backgroundColor: (sharedTheme ?? fallbackTheme).palette.background.default,
              },
            },
          }}
        >
          {items.map((item) => (
            <MenuItem key={item.value} value={item.value} sx={{ fontSize: 'inherit' }}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </ThemeProvider>
  );
};

/**
 * Builds a RevoGrid editor constructor bound to a fixed option list. Call once
 * per column at module scope — `columns` has to stay a stable reference.
 */
export const createSelectEditor = (options: readonly SelectOption[]) =>
  Editor((props: EditorType) => <SelectCellEditor {...props} options={options} />);
